import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { getErpJwtToken, getErpUserId } from 'app/services/auth/erp-auth-token.util';

export interface UsersCoordinationRow {
  id?: number;
  user_id_erp: number;
  station_id_erp: number;
  user_id_logiciel: number;
  station_id_logiciel: number;
  code_station?: string | null;
}

@Injectable({ providedIn: 'root' })
export class UsersCoordinationService {
  private readonly baseUrl = `${environment.apiUrl}api/users/coordination`;

  constructor(private readonly http: HttpClient) {}

  private authHeaders(): HttpHeaders {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const jwt = getErpJwtToken();
    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
    }
    return new HttpHeaders(headers);
  }

  /**
   * GET /api/users/coordination/resolve?user_id_erp=&station_id_erp=
   * Couple exact → IDs logiciel-caisse.
   */
  resolve(
    userIdErp: number,
    stationIdErp: number
  ): Observable<{ user_id_logiciel: number; station_id_logiciel: number; data: UsersCoordinationRow } | null> {
    const uid = Number(userIdErp);
    const sid = Number(stationIdErp);
    if (!Number.isFinite(uid) || uid <= 0 || !Number.isFinite(sid) || sid <= 0) {
      return of(null);
    }
    const params = new HttpParams()
      .set('user_id_erp', String(uid))
      .set('station_id_erp', String(sid));
    console.log('[LC Entrepot][coordination] resolve', { user_id_erp: uid, station_id_erp: sid });
    return this.http
      .get<{
        success?: boolean;
        user_id_logiciel?: number;
        station_id_logiciel?: number;
        data?: UsersCoordinationRow;
        message?: string;
      }>(`${this.baseUrl}/resolve`, {
        headers: this.authHeaders(),
        params,
      })
      .pipe(
        map((res) => {
          const lcUser = Number(res?.user_id_logiciel ?? res?.data?.user_id_logiciel);
          const lcStation = Number(res?.station_id_logiciel ?? res?.data?.station_id_logiciel);
          if (
            !res?.success ||
            !Number.isFinite(lcUser) ||
            lcUser <= 0 ||
            !Number.isFinite(lcStation) ||
            lcStation <= 0
          ) {
            console.warn('[LC Entrepot][coordination] resolve: pas de mapping', res);
            return null;
          }
          console.log('[LC Entrepot][coordination] resolve OK', {
            user_id_logiciel: lcUser,
            station_id_logiciel: lcStation,
          });
          return {
            user_id_logiciel: lcUser,
            station_id_logiciel: lcStation,
            data: res.data as UsersCoordinationRow,
          };
        }),
        catchError((err) => {
          const status = (err as { status?: number })?.status;
          if (status === 404) {
            console.warn('[LC Entrepot][coordination] resolve 404 — pas encore de ligne');
            return of(null);
          }
          console.error('[LC Entrepot][coordination] resolve erreur', err);
          return of(null);
        })
      );
  }

  upsert(payload: {
    user_id_erp: number;
    station_id_erp: number;
    user_id_logiciel: number;
    station_id_logiciel: number;
    code_station?: string;
  }): Observable<UsersCoordinationRow> {
    const erpUserId = getErpUserId();
    return this.http
      .post<{ success?: boolean; data?: UsersCoordinationRow; message?: string }>(
        this.baseUrl,
        payload,
        {
          headers: this.authHeaders(),
          params: erpUserId ? new HttpParams().set('id_user', String(erpUserId)) : undefined,
        }
      )
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.message || 'Upsert coordination échoué.');
          }
          return res.data;
        }),
        catchError((err) =>
          throwError(() => new Error((err as Error)?.message || 'Upsert coordination échoué.'))
        )
      );
  }
}
